import { type ChangeEvent, memo, useCallback, useId } from 'react';

import { cn } from '@air/utils-tailwind';

import {
  type WorkspaceAgentGuidedVariantColorOption,
  type WorkspaceAgentGuidedVariantColorOptionId,
} from '~/components/v2/components/workspace/workspace-agent-panel/utils/workspaceAgentGuidedVariantColor';

export type WorkspaceAgentGuidedVariantColorOptionFieldProps = {
  legend: string;
  onValueChange: (value: WorkspaceAgentGuidedVariantColorOptionId) => void;
  options: readonly WorkspaceAgentGuidedVariantColorOption[];
  value: WorkspaceAgentGuidedVariantColorOptionId;
};

export const WorkspaceAgentGuidedVariantColorOptionField = memo(
  ({ legend, onValueChange, options, value }: WorkspaceAgentGuidedVariantColorOptionFieldProps) => {
    const groupName = useId();

    const handleValueChange = useCallback(
      (event: ChangeEvent<HTMLInputElement>) => {
        const nextOption = options.find((option) => option.id === event.currentTarget.value);

        if (!nextOption) {
          return;
        }

        onValueChange(nextOption.id);
      },
      [onValueChange, options],
    );

    return (
      <fieldset className="m-0 flex min-w-0 flex-col gap-2 border-0 p-0">
        <legend className="font-medium ads:text-primary text-12 text-gray-12">{legend}</legend>
        {/* Narrow wrap matches the prototype: Transparent / White, then Black / Custom. */}
        <div className="flex max-w-[220px] flex-wrap gap-2">
          {options.map((option) => {
            const isSelected = option.id === value;

            return (
              <label
                className={cn(
                  'relative flex w-fit cursor-pointer items-center gap-1.5 rounded-full border ads:bg-base bg-gray-1 px-2.5 py-1 text-12 has-[:focus-visible]:outline-none ads:has-[:focus-visible]:ring-focus has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-blue-8',
                  isSelected
                    ? 'ads:border-primary border-blue-8 ads:text-accent text-blue-11'
                    : 'ads:border-input border-gray-5 ads:text-primary text-gray-12 ads:hover:border-hover-input hover:border-gray-6',
                )}
                key={option.id}
              >
                <input
                  checked={isSelected}
                  className="absolute inset-0 cursor-pointer opacity-0"
                  data-option-id={option.id}
                  data-testid="WORKSPACE_AGENT_GUIDED_VARIANT_COLOR_OPTION"
                  name={groupName}
                  onChange={handleValueChange}
                  type="radio"
                  value={option.id}
                />
                <span
                  aria-hidden
                  className="size-3.5 shrink-0 rounded-full border ads:border-strong border-gray-6"
                  style={option.swatchStyle}
                />
                {option.label}
              </label>
            );
          })}
        </div>
      </fieldset>
    );
  },
);

WorkspaceAgentGuidedVariantColorOptionField.displayName = 'WorkspaceAgentGuidedVariantColorOptionField';
